/**
 * IncidentModal Component
 *
 * Modal for creating and editing incidents based on quality markers and annotations
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { X, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select } from '@/components/ui/select';
import { useToast } from '@/contexts/ToastContext';
import { useApiClient } from '@/hooks/useApiClient';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { logError } from '@/lib/logger';
import type { ApiClient } from '@/lib/api';
import { CONTROL_SQUARE } from '@/components/ui/control-size';
import { cn } from '@/lib/utils';

export interface IncidentModalProps {
  analysisId: string;
  recordId: string;
  projectId: string;
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  initialMarkers?: string[];
  initialAnnotationId?: string;
  /**
   * Default severity, editable by the user — e.g. derived from the
   * record's current quality score. Falls back to 'medium' when omitted,
   * same as before this prop existed.
   */
  initialSeverity?: 'low' | 'medium' | 'high' | 'critical';
  /**
   * Caller-supplied apiClient (e.g. the embedded-mode client the caller
   * already resolved via useRecordDetail()). When omitted, falls back to
   * useApiClient()'s Clerk-based client — see IncidentModalWithHookClient
   * below, the ONLY place that hook is called, so it never runs where
   * there is no ClerkProvider. Embedded (Bitrix24) mode explicitly skips
   * ClerkProvider (main.tsx `isEmbeddedRoute`), and useApiClient() ->
   * useAuthFetch() -> useClerk() throws without one — calling it
   * unconditionally here would crash the embedded record-detail panel.
   */
  apiClient?: ApiClient;
}

export function IncidentModal({
  apiClient: apiClientProp,
  ...props
}: IncidentModalProps) {
  if (apiClientProp) {
    return <IncidentModalView {...props} apiClient={apiClientProp} />;
  }
  return <IncidentModalWithHookClient {...props} />;
}

// Isolates the useApiClient() call so it is only ever mounted when the
// caller did NOT supply an apiClient — never in embedded mode, where the
// caller always supplies one.
function IncidentModalWithHookClient(props: Omit<IncidentModalProps, 'apiClient'>) {
  const { apiClient } = useApiClient();
  return <IncidentModalView {...props} apiClient={apiClient} />;
}

interface IncidentModalViewProps extends Omit<IncidentModalProps, 'apiClient'> {
  apiClient: ApiClient;
}

function IncidentModalView({
  analysisId,
  recordId,
  projectId,
  isOpen,
  onClose,
  onSuccess,
  initialMarkers,
  initialAnnotationId,
  initialSeverity,
  apiClient,
}: IncidentModalViewProps) {
  const { t } = useTranslation(['common', 'alerts', 'records']);
  const { toast } = useToast();

  const [severity, setSeverity] = useState<'low' | 'medium' | 'high' | 'critical'>(
    initialSeverity ?? 'medium'
  );
  // Tracks whether the user has picked a severity themselves in the current
  // form instance. Without this, the resync effect below can't tell "the
  // caller sent a fresh default" apart from "the caller re-sent the same
  // prop reference after an unrelated re-render" — and silently overwriting
  // an explicit user choice is exactly the bug it exists to prevent.
  const [severityTouched, setSeverityTouched] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [creating, setCreating] = useState(false);

  // Stays in sync with the caller's default (e.g. record navigation changes
  // the quality score behind an already-mounted modal, or a background
  // re-analyze resolves while the form is open) — but only until the user
  // picks a severity themselves. Past that point the field is the user's,
  // not the caller's, and a later prop update (score landing, re-analyze
  // completing) must not silently snap it back.
  useEffect(() => {
    if (initialSeverity && !severityTouched) {
      setSeverity(initialSeverity);
    }
  }, [initialSeverity, severityTouched]);

  // Clear the form when the caller switches to a different record. This
  // modal is mounted unconditionally with no key={recordId} upstream
  // (RecordDetailInline just toggles `isOpen`, and now also closes it on
  // navigation), so without this a title/description typed for the old
  // record would resurface pre-filled the next time the form reopens for a
  // different one — and submitting would file a mismatched incident. Also
  // resets severityTouched: a new record's default deserves to apply once,
  // same as title/description starting blank again.
  useEffect(() => {
    setTitle('');
    setDescription('');
    setSeverityTouched(false);
  }, [recordId]);

  // Generate title from markers or annotation
  useEffect(() => {
    if (initialMarkers && initialMarkers.length > 0) {
      const markerLabels = initialMarkers
        .map((id) => {
          const markers: Record<string, string> = {
            missing_discovery: t('records:records.agentReview.markers.missingDiscovery'),
            weak_value_proposition: t(
              'records:records.agentReview.markers.weakValueProposition'
            ),
            poor_objection_handling: t(
              'records:records.agentReview.markers.poorObjectionHandling'
            ),
            tone_issues: t('records:records.agentReview.markers.toneIssues'),
            incomplete_follow_up: t(
              'records:records.agentReview.markers.incompleteFollowUp'
            ),
          };
          return markers[id] || id;
        })
        .join(', ');
      setTitle(`${t('incidents.incidentPrefix')}: ${markerLabels}`);
    } else if (initialAnnotationId) {
      setTitle(
        `${t('incidents.incidentPrefix')}: ${t('incidents.qualityIssueDetected')}`
      );
    }
  }, [initialMarkers, initialAnnotationId, t]);

  const handleSave = async () => {
    if (!title.trim()) {
      toast.error(t('incidents.titleRequired'), t('incidents.titleRequiredMessage'));
      return;
    }

    try {
      setCreating(true);

      const response = await apiClient.incidents.create({
        analysisId,
        recordId,
        projectId,
        severity,
        title: title.trim(),
        description: description.trim() || undefined,
        status: 'open',
      });

      toast.success(
        t('incidents.created'),
        t('incidents.createdMessage', { title: response.title })
      );
      onSuccess?.();
      onClose();
    } catch (err) {
      logError(
        'incidents.create_failed',
        { analysisId, recordId, projectId },
        err instanceof Error ? err : undefined
      );
      toast.error(
        t('incidents.createError'),
        err instanceof Error ? err.message : t('common:common.errorUnknown')
      );
    } finally {
      setCreating(false);
    }
  };

  // Focus trap (WCAG 2.1.2) — keeps Tab focus within the dialog while open,
  // same as AnnotationModal.
  const containerRef = useFocusTrap<HTMLDivElement>(isOpen);

  // Escape closes the dialog — same in-flight-save guard as the close
  // button below, so Escape can't bypass a save the button itself blocks.
  useEffect(() => {
    if (!isOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !creating) onClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isOpen, creating, onClose]);

  if (!isOpen) return null;

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby="incident-modal-title"
      data-testid="incident-modal-backdrop"
      className="fixed inset-0 z-modal flex items-center justify-center bg-bg-void/50"
    >
      <Card
        data-testid="incident-modal"
        className="w-full max-w-2xl p-6 bg-bg-surface border-border"
      >
        <div
          data-testid="incident-modal-header"
          className="flex items-center justify-between mb-4"
        >
          <h2
            id="incident-modal-title"
            data-testid="incident-modal-title"
            className="text-base font-medium text-fg flex items-center gap-2"
          >
            <AlertTriangle className="h-5 w-5 text-warning" />
            {t('incidents.createIncident')}
          </h2>
          <button
            data-testid="incident-modal-close-button"
            onClick={onClose}
            className={cn(
              CONTROL_SQUARE.md,
              'flex items-center justify-center rounded-button hover:bg-bg-elevated transition-all duration-150 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
            )}
            aria-label={t('accessibility.actions.close')}
            disabled={creating}
          >
            <X className="h-5 w-5 text-fg-muted" />
          </button>
        </div>

        <div data-testid="incident-modal-form" className="space-y-4">
          {/* Severity */}
          <div>
            <Label
              htmlFor="incident-severity"
              className="text-sm font-medium text-fg mb-2 block"
            >
              {t('common:common.severity')}
            </Label>
            <Select
              data-testid="incident-modal-severity-select"
              value={severity}
              onValueChange={(val) => {
                setSeverity(val as 'low' | 'medium' | 'high' | 'critical');
                setSeverityTouched(true);
              }}
              disabled={creating}
              options={[
                { value: 'low', label: t('common:common.low') },
                { value: 'medium', label: t('common:common.medium') },
                { value: 'high', label: t('common:common.high') },
                { value: 'critical', label: t('alerts:alerts.critical') },
              ]}
              size="sm"
            />
          </div>

          {/* Title */}
          <div>
            <Label
              htmlFor="incident-title"
              className="text-sm font-medium text-fg mb-2 block"
            >
              {t('common:common.title')} *
            </Label>
            <Input
              data-testid="incident-modal-title-input"
              id="incident-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('incidents.titlePlaceholder')}
              maxLength={200}
              disabled={creating}
            />
          </div>

          {/* Description */}
          <div>
            <Label
              htmlFor="incident-description"
              className="text-sm font-medium text-fg mb-2 block"
            >
              {t('common:common.description')}
            </Label>
            <Textarea
              data-testid="incident-modal-description-textarea"
              id="incident-description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={t('incidents.descriptionPlaceholder')}
              minRows={4}
              maxLength={2000}
              disabled={creating}
            />
            <p
              data-testid="incident-modal-description-counter"
              className="mt-1 text-xs text-fg-muted"
            >
              {t('incidents.charactersCount', { count: description.length, max: 2000 })}
            </p>
          </div>

          {/* Action Buttons */}
          <div
            data-testid="incident-modal-actions"
            className="flex justify-end gap-3 pt-4"
          >
            <Button
              data-testid="incident-modal-cancel-button"
              onClick={onClose}
              variant="outline"
              size="sm"
              disabled={creating}
            >
              {t('common:common.cancel')}
            </Button>
            <Button
              data-testid="incident-modal-submit-button"
              onClick={handleSave}
              isLoading={creating}
              disabled={creating || !title.trim()}
              size="sm"
            >
              {!creating && <AlertTriangle className="h-4 w-4 mr-2" />}
              {t('incidents.createIncident')}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
